import spartaTransportUtility from '@sparta.global.utilities/sparta-transport-utility/dist/es6.js';
import benchmarkUtility from '@sparta.sales.utilities/benchmark-utility/dist/es6.js';

/* eslint-disable no-console */
const TRACE = 'TRACE';
const DEBUG = 'DEBUG';
const INFO = 'INFO';
const WARNING = 'WARNING';
const ERROR = 'ERROR';

/**
 * @name        Remote Logging Utility
 * @file        remote-logging-utility.js
 * @description Log information to a remote server
 * @classdesc   Log information to a remote server
 * @global      Creates a global remoteConsole singleton object.
 */

/** Class for the RemoteLoggingUtility */
class RemoteLoggingUtility {
  /**
   * Creates an instance of the RemoteLoggingUtility
   * @returns { void }
   */
  constructor() {
    this.Transport = spartaTransportUtility;
    this.echoInBrowser = spaParams.siteParams.remoteLogging ? spaParams.siteParams.remoteLogging.echoInBrowser || false : false;
    if (spaParams.siteParams.jsTimer) {
      benchmarkUtility.init(this);
    }
  }

  /**
   * @description Log to remote endpoint.
   * @method logRemote
   * @param {Object} msg message to log
   * @param {String} level severity of the log
   * @testExample logRemote('Something failed.', 1)
   * @testOutput equal true
   * @mockSpaParams {
   *   siteParams: { remoteLogging: { enabled: true, endpoint: 'http://localhost/log' } }
   * }
   * @testExpect this.Transport.once.called toEqual true
   * @spy this.Transport.once
   * @returns {boolean} true if no error, false otherwise
   */
  logRemote(msg, level) {
    if (!spaParams.siteParams.remoteLogging) return false;
    if (!spaParams.siteParams.remoteLogging.enabled) return false;
    const payload = {
      logSeverity: level,
      logOrigin: spaParams.siteParams.remoteLogging.origin,
      logMessage: `${msg} | ${window.location.href}`
    };

    // Error and Success handlers intentionally omitted. We're simply passing the log to a service.
    this.Transport.once({
      url: spaParams.siteParams.remoteLogging.endpoint,
      method: 'POST',
      contentType: 'application/json',
      dataType: 'json',
      data: JSON.stringify(payload)
    });
    return true;
  }

  /**
   * @description Log stack trace.
   * @method trace
   * @param {String} msg Message.
   * @param {Boolean} [echoInBrowser] Should log in browser.
   * @testExample trace('test message', true)
   * @testOutput undefined
   * @testExpect this.logRemote.called toEqual true
   * @testExpect console.trace.called toEqual true
   * @spy this.logRemote
   * @spy console.trace
   * @returns {void}
   */
  trace(msg, echoInBrowser = this.echoInBrowser) {
    this.logRemote(msg, TRACE);
    if (echoInBrowser) {
      console.trace(msg);
    }
  }

  /**
   * @description Log message.
   * @method log
   * @param {String} msg Message.
   * @param {Boolean} [echoInBrowser] Should log in browser.
   * @testExample log('test message', true)
   * @testOutput undefined
   * @testExpect this.logRemote.called toEqual true
   * @testExpect console.log.called toEqual true
   * @spy this.logRemote
   * @spy console.log
   * @returns {void}
   */
  log(msg, echoInBrowser = this.echoInBrowser) {
    this.logRemote(msg, DEBUG);
    if (echoInBrowser) {
      console.log(msg);
    }
  }

  /**
   * @description Log info.
   * @method info
   * @param {String} msg Message.
   * @param {Boolean} [echoInBrowser] Should log in browser.
   * @testExample info('test message', true)
   * @testOutput undefined
   * @testExpect this.logRemote.called toEqual true
   * @testExpect console.info.called toEqual true
   * @spy this.logRemote
   * @spy console.info
   * @returns {void}
   */
  info(msg, echoInBrowser = this.echoInBrowser) {
    this.logRemote(msg, INFO);
    if (echoInBrowser) {
      console.info(msg);
    }
  }

  /**
   * @description Log warning.
   * @warn
   * @param {String} msg Message.
   * @param {Boolean} [echoInBrowser] Should log in browser.
   * @testExample warn('test message', true)
   * @testOutput undefined
   * @testExpect this.logRemote.called toEqual true
   * @testExpect console.warn.called toEqual true
   * @spy this.logRemote
   * @spy console.warn
   * @returns {void}
   */
  warn(msg, echoInBrowser = this.echoInBrowser) {
    this.logRemote(msg, WARNING);
    if (echoInBrowser) {
      console.warn(msg);
    }
  }

  /**
   * @description Log error.
   * @method error
   * @param {String} msg Message.
   * @param {Boolean} [echoInBrowser] Should log in browser.
   * @testExample error('test message', true)
   * @testOutput undefined
   * @testExpect this.logRemote.called toEqual true
   * @testExpect console.error.called toEqual true
   * @spy this.logRemote
   * @spy console.error
   * @returns {void}
   */
  error(msg, echoInBrowser = this.echoInBrowser) {
    this.logRemote(msg, ERROR);
    if (echoInBrowser) {
      console.error(msg);
    }
  }
}
var index = new RemoteLoggingUtility();

export { index as default };
